import {
  ChoiceConfig,
  FormCategory,
  FormSchema,
  FormTemplateField,
  SectionDescriptionConfig,
  SectionHeaderConfig,
  SignatureConfig,
} from "../types/formTemplates";

export interface ValidationError {
  fieldId?: string;
  path: string;
  message: string;
}

export interface ValidationResult {
  ok: boolean;
  errors: ValidationError[];
}

const LAYOUT_ONLY: ReadonlySet<FormTemplateField["type"]> = new Set<
  FormTemplateField["type"]
>(["section_header", "section_description", "divider", "page_break"]);

const CHOICE_TYPES: ReadonlySet<FormTemplateField["type"]> = new Set<
  FormTemplateField["type"]
>(["single_choice", "multiple_choice", "dropdown"]);

export interface TemplateDraft {
  name: string;
  category: FormCategory;
  schema: FormSchema;
}

// Mirrors backend validation rules (source of truth) so users see inline
// errors before hitting the API.
export function validateTemplate(draft: TemplateDraft): ValidationResult {
  const errors: ValidationError[] = [];

  const trimmedName = draft.name.trim();
  if (!trimmedName) {
    errors.push({
      path: "name",
      message: "Template name is required.",
    });
  } else if (trimmedName.length > 120) {
    errors.push({
      path: "name",
      message: "Template name must be 120 characters or fewer.",
    });
  }

  if (
    ![
      "reusable",
      "per_visit",
      "per_visit_clinical",
      "post_appointment",
    ].includes(draft.category)
  ) {
    errors.push({
      path: "category",
      message:
        "Pick a template type: Reusable, Per-Visit, Clinical Notes (SOAP), or Post-Appointment Form.",
    });
  }

  const fields = draft.schema.fields;
  if (fields.length > 200) {
    errors.push({
      path: "schema.fields",
      message: "A template may contain at most 200 fields.",
    });
  }

  const idsSeen = new Set<string>();
  for (const field of fields) {
    if (idsSeen.has(field.id)) {
      errors.push({
        fieldId: field.id,
        path: `schema.fields.${field.id}`,
        message: "Duplicate field id.",
      });
    }
    idsSeen.add(field.id);
    errors.push(...validateField(field));
  }

  return { ok: errors.length === 0, errors };
}

export function validateField(field: FormTemplateField): ValidationError[] {
  const errors: ValidationError[] = [];
  const isLayout = LAYOUT_ONLY.has(field.type);

  if (!isLayout) {
    if (!field.label || !field.label.trim()) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.label`,
        message: "Field label is required.",
      });
    }
    if (field.actor && !["staff", "client"].includes(field.actor)) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.actor`,
        message: "Actor must be staff or client.",
      });
    }
  }

  if (field.layout?.colSpan && ![6, 12].includes(field.layout.colSpan)) {
    errors.push({
      fieldId: field.id,
      path: `${field.id}.layout.colSpan`,
      message: "Column span must be 6 or 12.",
    });
  }

  if (CHOICE_TYPES.has(field.type)) {
    const cfg = field.config as ChoiceConfig;
    if (!cfg || !Array.isArray(cfg.options) || cfg.options.length === 0) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.config.options`,
        message: "Choice fields need at least one option.",
      });
    } else {
      if (cfg.options.length > 50) {
        errors.push({
          fieldId: field.id,
          path: `${field.id}.config.options`,
          message: "A choice field can have at most 50 options.",
        });
      }
      const seen = new Set<string>();
      for (const opt of cfg.options) {
        if (!opt.id || !opt.id.trim()) {
          errors.push({
            fieldId: field.id,
            path: `${field.id}.config.options`,
            message: "Every option needs a non-empty id.",
          });
          continue;
        }
        if (seen.has(opt.id)) {
          errors.push({
            fieldId: field.id,
            path: `${field.id}.config.options.${opt.id}`,
            message: `Duplicate option id '${opt.id}'.`,
          });
        }
        seen.add(opt.id);
        if (!opt.label || !opt.label.trim()) {
          errors.push({
            fieldId: field.id,
            path: `${field.id}.config.options.${opt.id}`,
            message: "Option labels cannot be blank.",
          });
        }
      }
    }
  }

  if (field.type === "signature") {
    const cfg = field.config as SignatureConfig;
    if (!cfg || !["staff", "client"].includes(cfg.signatory)) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.config.signatory`,
        message: "Signature signatory must be staff or client.",
      });
    }
  }

  if (field.type === "section_header") {
    const cfg = field.config as SectionHeaderConfig;
    if (!cfg || !cfg.text || !cfg.text.trim()) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.config.text`,
        message: "Section headers need a heading.",
      });
    }
  }

  if (field.type === "section_description") {
    const cfg = field.config as SectionDescriptionConfig;
    if (!cfg || !cfg.text || !cfg.text.trim()) {
      errors.push({
        fieldId: field.id,
        path: `${field.id}.config.text`,
        message: "Section descriptions need body text.",
      });
    }
  }

  return errors;
}

export function groupErrorsByFieldId(
  errors: ValidationError[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const e of errors) {
    if (!e.fieldId) continue;
    (out[e.fieldId] = out[e.fieldId] || []).push(e.message);
  }
  return out;
}
